import React, { useCallback, useState } from 'react'
import { Form, OverlayTrigger, Tooltip as BootstrapTooltip } from 'react-bootstrap'
import { EditorState } from 'lexical'

import { useInteractWithAiAgentMutation } from '../../../store/ai/aiAgent'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { addToast } from '../../../store/toast/toastSlice'
import {
  selectNewEmailAiAgentId,
  selectNewEmailCitationIsLoading,
  selectNewEmailEditing,
  selectNewEmailEditingLoading,
  selectNewEmailInteractionLoading,
  selectNewEmailResult,
  setEditingIsLoading,
  setEditingNewEmail,
  setNewEmailAiAgentId,
  setNewEmailResult,
} from '../../../store/emailHub/newEmail'

import { RichTextToolbarPlugin } from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/Plugins/RichTextToolbarPlugin/RichTextToolbarPlugin'
import { WritableLexicalEditor } from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/WritableLexicalEditor/WritableLexicalEditor'
import { ReadOnlyLexicalEditor } from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/ReadOnlyLexicalEditor/ReadOnlyLexicalEditor'
import { CitationNodeContext } from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/Nodes/CitationNode/CitationNodeContext'
import { EditablePlugin } from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/Plugins/EditablePlugin/EditablePlugin'
import { getEditorHtmlContent, getTextContentFromState } from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/utils'
import { CopyContent, HTML_TYPE, TEXT_TYPE, writeCopyContentToClipboard } from '../../../apps/professional-app/fa/MeetingSummary/copy'
import { CitationNode } from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/Nodes/CitationNode/CitationNode'
import { LEXICAL_NODES } from '../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/nodes'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'

import { Divider } from '../../../components/Divider/Divider'
import { Button } from '../../../components/Button/Button'

import { NewEmailInteractionResult } from '../../../models/NewEmail'
import { AIAgentType } from '../../../models/AIAgent'

import { AIAgentResultCitations } from '../../AIAgent/AIAgentResultCitations/AIAgentResultCitations'

import { useNewEmailResult } from './hooks'
import { addMessage } from '../../../store/streamingChat/streamingChat'

const NODES = [...LEXICAL_NODES, CitationNode]
const IGNORE_CITATION_CONFIG = {
  html: {
    export: new Map([[CitationNode, () => ({ element: null })]]),
  },
}

export const NewEmailEditor: React.FC = () => {
  const dispatch = useAppDispatch()
  const { tracked } = useTracking()

  const accountId = useAppSelector(selectAccountId)
  const newEmailResult = useAppSelector(selectNewEmailResult)
  const aiAgentId = useAppSelector(selectNewEmailAiAgentId)

  const [editedSubject, setEditedSubject] = useState<string | null>(null)
  const [editedEmail, setEditedEmail] = useState<string | null>(null)

  const [triggerInteractWithAiAgent] = useInteractWithAiAgentMutation()

  const _handleNewEmailEditInteraction = useCallback(async () => {
    if (!aiAgentId) {
      return
    }
    dispatch(setEditingIsLoading(true))
    try {
      const newEmailInteractionResult = (await triggerInteractWithAiAgent({
        accountId,
        aiAgentId,
        payload: { type: 'edit', email: editedEmail, subject: editedSubject },
      }).unwrap()) as NewEmailInteractionResult

      const { newAiAgentId } = newEmailInteractionResult
      if (newAiAgentId) {
        dispatch(setNewEmailAiAgentId(newAiAgentId))
      }

      const { result } = newEmailInteractionResult
      const { message } = result
      dispatch(setNewEmailResult(result))
      if (message) {
        dispatch(addMessage({ type: 'agent', message }))
      }

      return newEmailInteractionResult
    } catch (e) {
      console.error(e)
    } finally {
      dispatch(setEditingIsLoading(false))
    }
  }, [accountId, aiAgentId, dispatch, editedEmail, editedSubject, triggerInteractWithAiAgent])

  const handleNewEmailEditInteraction = tracked(_handleNewEmailEditInteraction, {
    eventName: TrackEvent.NEW_EMAIL_INTERACTED_WITH,
    eventDetails: { type: 'edit', editedEmail, editedSubject },
  })

  const handleSave = useCallback(async () => {
    const interactionResult = await handleNewEmailEditInteraction()
    if (interactionResult) {
      dispatch(setEditingNewEmail(false))
      setEditedEmail(null)
      setEditedSubject(null)
    }
  }, [dispatch, handleNewEmailEditInteraction])

  const handleEdit = () => {
    const { email = '', subject = '' } = newEmailResult ?? {}
    setEditedEmail(email)
    setEditedSubject(subject)
    dispatch(setEditingNewEmail(true))
  }

  const handleCancel = () => {
    dispatch(setEditingNewEmail(false))
    setEditedEmail(null)
    setEditedSubject(null)
  }

  const handleEmailChange = (editorState: EditorState) => {
    setEditedEmail(getEditorHtmlContent(editorState, NODES))
  }

  const handleSubjectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedSubject(e.target.value)
  }

  return (
    <div className='d-flex flex-column zocks-p-2.0 gap-4 zocks-border-0.5 email-response-main-container'>
      <NewEmailEditorSubjectRow editedSubject={editedSubject ?? ''} onChange={handleSubjectChange} />
      <Divider />
      <NewEmailEditorBodyRow onChange={handleEmailChange} />
      <NewEmailEditorButtonRow onEdit={handleEdit} onCancel={handleCancel} onSave={handleSave} editedEmail={editedEmail} />
      <NewEmailEditorCitationRow />
    </div>
  )
}

interface NewEmailEditorSubjectRowProps {
  editedSubject: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}

const NewEmailEditorSubjectRow: React.FC<NewEmailEditorSubjectRowProps> = ({ editedSubject, onChange }) => {
  const newEmailResult = useAppSelector(selectNewEmailResult)
  const isEditing = useAppSelector(selectNewEmailEditing)

  if (!newEmailResult) {
    return null
  }

  if (isEditing) {
    return (
      <div className='d-flex flex-column'>
        <Form.Label className='label-medium'>Subject</Form.Label>
        <Form.Control className='paragraph-medium zocks-p-0.75' value={editedSubject} onChange={onChange} />
      </div>
    )
  }

  const { subject } = newEmailResult

  return <h5>{subject}</h5>
}

interface NewEmailEditorBodyRowProps {
  onChange: (editorState: EditorState) => void
}

const NewEmailEditorBodyRow: React.FC<NewEmailEditorBodyRowProps> = ({ onChange }) => {
  const interactionIsLoading = useAppSelector(selectNewEmailInteractionLoading)
  const editingIsLoading = useAppSelector(selectNewEmailEditingLoading)
  const isEditing = useAppSelector(selectNewEmailEditing)

  const { newEmailState, filteredCitations } = useNewEmailResult()

  if (isEditing) {
    return (
      <CitationNodeContext.Provider value={{ citations: interactionIsLoading ? [] : filteredCitations }}>
        <WritableLexicalEditor
          key={newEmailState.displayId}
          onChange={onChange}
          initialEditorState={newEmailState.editorState}
          className='p-0 border-0'
          containerClassName='new-email-editor-container '
          lexicalNodes={NODES}
          header={<RichTextToolbarPlugin className='pb-4 gap-2' disabled={interactionIsLoading || editingIsLoading} />}
        >
          <EditablePlugin isEditable={!interactionIsLoading && !editingIsLoading} />
        </WritableLexicalEditor>
      </CitationNodeContext.Provider>
    )
  }

  return (
    <div className='d-flex flex-column gap-2 new-email-result-container h-100'>
      <div className='new-email-result-inner-container pb-1 flex-grow-1'>
        <CitationNodeContext.Provider value={{ citations: interactionIsLoading ? [] : filteredCitations }}>
          <ReadOnlyLexicalEditor key={newEmailState.displayId} initialEditorState={newEmailState.editorState} lexicalNodes={NODES} />
        </CitationNodeContext.Provider>
      </div>
    </div>
  )
}

interface NewEmailEditorButtonRowProps {
  onEdit: () => void
  onCancel: () => void
  onSave: () => Promise<void>
  editedEmail: string | null
}

const NewEmailEditorButtonRow: React.FC<NewEmailEditorButtonRowProps> = ({ onEdit, onCancel, onSave, editedEmail }) => {
  const dispatch = useAppDispatch()

  const interactionIsLoading = useAppSelector(selectNewEmailInteractionLoading)
  const citationIsLoading = useAppSelector(selectNewEmailCitationIsLoading)
  const isEditing = useAppSelector(selectNewEmailEditing)

  const { newEmailState } = useNewEmailResult()

  const handleCopy = async () => {
    const copyContent = getCopyContent()
    if (!copyContent) {
      return
    }
    await copyContentToClipboard(copyContent)
  }

  const getCopyContent = () => {
    const lexicalState = JSON.stringify(newEmailState.editorState)
    return {
      [TEXT_TYPE]: getTextContentFromState(lexicalState, NODES),
      [HTML_TYPE]: getEditorHtmlContent(newEmailState.editorState, NODES, IGNORE_CITATION_CONFIG) ?? '',
    }
  }

  const copyContentToClipboard = async (copyContent: CopyContent) => {
    await writeCopyContentToClipboard(copyContent)
    dispatch(addToast({ variant: 'success', title: 'Copied', messages: ['Copied to Clipboard!'] }))
  }

  if (isEditing) {
    return (
      <div className='d-flex gap-2 justify-content-end'>
        <Button variant='secondary' onClick={onCancel} disabled={interactionIsLoading}>
          Cancel
        </Button>
        <Button variant='primary' onClick={onSave} disabled={!editedEmail} loading={interactionIsLoading}>
          Save
        </Button>
      </div>
    )
  }

  return (
    <div className='d-flex gap-2'>
      <OverlayTrigger
        trigger={citationIsLoading ? ['hover', 'focus'] : []}
        placement='top'
        overlay={<BootstrapTooltip className='zocks-text-gray-500'>Editing is disabled while references are being generated. Please hold on.</BootstrapTooltip>}
      >
        <Button className='button-medium' variant='header' onClick={onEdit} disabled={interactionIsLoading || citationIsLoading}>
          <i className='bi bi-pencil-square' />
          Edit
        </Button>
      </OverlayTrigger>
      <Button variant='header' className='button-medium' onClick={handleCopy}>
        <i className='bi bi-copy' />
      </Button>
    </div>
  )
}

const NewEmailEditorCitationRow = () => {
  const citationIsLoading = useAppSelector(selectNewEmailCitationIsLoading)
  const isEditing = useAppSelector(selectNewEmailEditing)

  const { filteredCitations } = useNewEmailResult()

  if (isEditing) {
    return null
  }

  return <AIAgentResultCitations aiAgentType={AIAgentType.NEW_EMAIL} citationIsLoading={citationIsLoading} citations={filteredCitations} />
}
